import { Link, WhenVisible } from "@inertiajs/react";
import { FileText, LoaderCircle, Plus } from "lucide-react";

import GroupGate from "@/components/group-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GROUPS } from "@/constants/groups";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { PurchaseRequestInvoiceSummary } from "@/types";

interface InvoicesTableProps {
  invoices?: PurchaseRequestInvoiceSummary[];
  purchaseRequestId?: number;
}

export default function InvoicesTable({ invoices, purchaseRequestId }: InvoicesTableProps) {
  const routes = useOrganizationRoutes();
  const addInvoiceUrl = purchaseRequestId 
    ? routes.invoices.uploadNew({ purchase_request_id: purchaseRequestId })
    : routes.invoices.uploadNew();

  return (
    <GroupGate anyOf={[GROUPS.buyer, GROUPS.supervisor]}>
      <WhenVisible
        data="invoices"
        fallback={
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Facturas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-8 text-center text-muted-foreground">
                <LoaderCircle className="mx-auto mb-4 h-8 w-8 animate-spin" />
                <p className="text-sm">Cargando facturas...</p>
              </div>
            </CardContent>
          </Card>
        }
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Facturas
                </CardTitle>
                <CardDescription>
                  Facturas vinculadas a esta solicitud de compra
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" asChild>
                <Link href={addInvoiceUrl}>
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar factura
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {invoices && invoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Número de factura</TableHead>
                    <TableHead>Fecha de factura</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Cargado por</TableHead>
                    <TableHead>Revisado por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="cursor-pointer">
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full font-medium"
                        >
                          #{invoice.id}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full"
                        >
                          {invoice.invoice_number ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full"
                        >
                          {invoice.invoice_date ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full"
                        >
                          {invoice.supplier?.name ?? "Sin proveedor"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full"
                        >
                          {invoice.created_by?.full_name ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.invoices.show(invoice.id)}
                          className="block h-full w-full"
                        >
                          {invoice.reviewed_by?.full_name ?? "—"}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <FileText />
                  </EmptyMedia>
                  <EmptyTitle>No hay facturas relacionadas</EmptyTitle>
                  <EmptyDescription>
                    Las facturas aparecerán aquí cuando sean vinculadas a esta
                    solicitud
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button asChild variant="default" className="w-full sm:w-auto">
                      <Link href={addInvoiceUrl}>
                        <Plus className="h-4 w-4 mr-1" />
                        Agregar factura
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full sm:w-auto">
                      <Link href={routes.invoices.index()}>Ver todas las facturas</Link>
                    </Button>
                  </div>
                </EmptyContent>
              </Empty>
            )}
          </CardContent>
        </Card>
      </WhenVisible>
    </GroupGate>
  );
}
